import { Stack } from '@startuptools/ui/Stack';
import { Trans } from '@lingui/react';
import { GqlDigitalAgm } from '../../../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/joy/Button';
import { Checkbox } from '@startuptools/ui/joy/Checkbox';
import { Controller } from 'react-hook-form';
import { Select } from '@startuptools/ui/joy/Select';
import { Option } from '@startuptools/ui/joy/Option';
import { isNil, range } from 'lodash-es';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { Input } from '@startuptools/ui/joy/Input';
import { Radio } from '@startuptools/ui/joy/Radio';
import { RadioGroup } from '@startuptools/ui/joy/RadioGroup';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import { List } from '@startuptools/ui/joy/List';
import { ListItem } from '@startuptools/ui/joy/ListItem';
import { UpdateField } from '../UpdateField';
import { StepProps } from '../../types';

export const StepSummons = ({ form, onSave, loading }: StepProps) => {
  const summonTimes = range(2, 7).map(String);

  return (
    <form
      onSubmit={form.handleSubmit(data => {
        const hasAtLeastOneMethod =
          data.summoningMethodMail ||
          data.summoningMethodEmail ||
          data.summoningMethodFax ||
          data.summoningMethodPoit ||
          !isNil(data.summoningMethodNewspaper) ||
          !isNil(data.summoningMethodOtherPrimaryLang);

        if (!hasAtLeastOneMethod) {
          form.setError('root', {
            type: 'custom',
            message: 'at_least_one_required',
          });
          return;
        }

        return onSave({
          summoningTimeMin: data.summoningTimeMin,
          summoningTimeMax: data.summoningTimeMax,
          summoningMethodMail: data.summoningMethodMail,
          summoningMethodEmail: data.summoningMethodEmail,
          summoningMethodFax: data.summoningMethodFax,
          summoningMethodPoit: data.summoningMethodPoit,
          summoningMethodNewspaper: data.summoningMethodNewspaper,
          summoningMethodOtherPrimaryLang: data.summoningMethodOtherPrimaryLang,
          digitalAgm: data.digitalAgm,
          changeSummoning: data.changeSummoning,
        });
      })}
    >
      <Stack gap={2}>
        <Controller
          control={form.control}
          name="changeSummoning"
          key="changeSummoning"
          render={({ field }) => (
            <UpdateField value={field.value} onChange={field.onChange} label={<Trans id="summoning" />} />
          )}
        />
        {(form.formState.errors.summoningTimeMax || form.formState.errors.summoningTimeMin) && (
          <FormControl error>
            {form.formState.errors.summoningTimeMax?.message === 'max_greater_than_min' && (
              <FormHelperText>
                <Trans id="The maximum time for summoning must be greater than the minimum time" />
              </FormHelperText>
            )}
            {(form.formState.errors.summoningTimeMax?.type === 'required' ||
              form.formState.errors.summoningTimeMin?.type === 'required') && (
              <FormHelperText>
                <Trans id="Both values are required" />
              </FormHelperText>
            )}
          </FormControl>
        )}
        <FormLabel>
          <Trans
            id="Minimum time for summoning {minTime} and at most {maxTime} before the annual general meeting"
            values={{
              minTime: (
                <Controller
                  control={form.control}
                  name="summoningTimeMin"
                  key="summoningTimeMin"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl
                      error={!!form.formState.errors.summoningTimeMin}
                      sx={{
                        display: 'inline-flex',
                        mx: '4px',
                      }}
                    >
                      <Select
                        size="sm"
                        onChange={(_, value) => field.onChange(value)}
                        value={field.value}
                        placeholder={
                          <>
                            {'...'} <Trans id="weeks" />
                          </>
                        }
                      >
                        {summonTimes.map(weeks => (
                          <Option key={weeks} value={weeks}>
                            {weeks} <Trans id="weeks" />
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              ),
              maxTime: (
                <Controller
                  control={form.control}
                  name="summoningTimeMax"
                  key="summoningTimeMax"
                  rules={{
                    required: true,
                    validate: value => {
                      const min = form.getValues('summoningTimeMin');
                      if (value && min && parseInt(value) < parseInt(min)) {
                        return 'max_greater_than_min';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <FormControl
                      error={!!form.formState.errors.summoningTimeMax}
                      sx={{
                        display: 'inline-flex',
                        mx: '4px',
                      }}
                    >
                      <Select
                        size="sm"
                        onChange={(_, value) => field.onChange(value)}
                        value={field.value}
                        placeholder={
                          <>
                            {'...'} <Trans id="weeks" />
                          </>
                        }
                      >
                        {summonTimes.map(weeks => (
                          <Option key={weeks} value={weeks}>
                            {weeks} <Trans id="weeks" />
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              ),
            }}
          />
        </FormLabel>

        <Stack gap={1}>
          <FormControl error={Boolean(form.formState.errors.root?.message === 'at_least_one_required')}>
            <FormLabel>
              <Trans id="Summons shall be made by:" />
            </FormLabel>
            {form.formState.errors.root?.message === 'at_least_one_required' && (
              <FormHelperText>
                <Trans id="At least one summons method must be selected" />
              </FormHelperText>
            )}
          </FormControl>

          <div role="group">
            <List size="sm">
              <ListItem>
                <Controller
                  control={form.control}
                  name="summoningMethodMail"
                  key="summoningMethodMail"
                  render={({ field }) => (
                    <Checkbox checked={field.value ?? false} onChange={field.onChange} label={<Trans id="Post" />} />
                  )}
                />
              </ListItem>

              <ListItem>
                <Controller
                  control={form.control}
                  name="summoningMethodEmail"
                  key="summoningMethodEmail"
                  render={({ field }) => (
                    <Checkbox checked={field.value ?? false} onChange={field.onChange} label={<Trans id="Email" />} />
                  )}
                />
              </ListItem>

              <ListItem>
                <Controller
                  control={form.control}
                  name="summoningMethodFax"
                  key="summoningMethodFax"
                  render={({ field }) => (
                    <Checkbox checked={field.value ?? false} onChange={field.onChange} label={<Trans id="Fax" />} />
                  )}
                />
              </ListItem>

              <ListItem>
                <Controller
                  control={form.control}
                  name="summoningMethodPoit"
                  key="summoningMethodPoit"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value ?? false}
                      onChange={field.onChange}
                      label={<Trans id="Post- och Inrikes Tidningar" />}
                    />
                  )}
                />
              </ListItem>

              <ListItem>
                <Stack direction="row" gap={2} alignItems="center">
                  <Controller
                    control={form.control}
                    name="summoningMethodNewspaper"
                    key="summoningMethodNewspaper"
                    rules={{
                      validate: value => {
                        if (!isNil(value) && value.trim() === '') {
                          return 'required';
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Checkbox
                          checked={!isNil(field.value)}
                          onChange={event => field.onChange(event.target.checked ? '' : undefined)}
                          label={<Trans id="Newspaper" />}
                          sx={{ width: '120px' }}
                        />
                        <FormControl sx={{ width: '300px' }} error={!!form.formState.errors.summoningMethodNewspaper}>
                          <Input
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value)}
                            size="sm"
                            disabled={isNil(field.value)}
                          />
                          {form.formState.errors.summoningMethodNewspaper && (
                            <FormHelperText>
                              <Trans id="Please enter a newspaper name" />
                            </FormHelperText>
                          )}
                        </FormControl>
                      </>
                    )}
                  />
                </Stack>
              </ListItem>

              <ListItem>
                <Stack direction="row" gap={2} alignItems="center">
                  <Controller
                    control={form.control}
                    name="summoningMethodOtherPrimaryLang"
                    key="summoningMethodOtherPrimaryLang"
                    rules={{
                      validate: value => {
                        if (!isNil(value) && value.trim() === '') {
                          return 'required';
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Checkbox
                          checked={!isNil(field.value)}
                          onChange={event => field.onChange(event.target.checked ? '' : undefined)}
                          label={<Trans id="Other" />}
                          sx={{ width: '120px' }}
                        />
                        <FormControl
                          sx={{ width: '300px' }}
                          error={!!form.formState.errors.summoningMethodOtherPrimaryLang}
                        >
                          <Input
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value)}
                            size="sm"
                            disabled={isNil(field.value)}
                          />
                          {form.formState.errors.summoningMethodOtherPrimaryLang && (
                            <FormHelperText>
                              <Trans id="Please specify the other method" />
                            </FormHelperText>
                          )}
                        </FormControl>
                      </>
                    )}
                  />
                </Stack>
              </ListItem>
            </List>
          </div>
        </Stack>

        <Stack gap={1}>
          <Controller
            control={form.control}
            name="digitalAgm"
            key="digitalAgm"
            rules={{ required: true }}
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors.digitalAgm)}>
                <FormLabel>
                  <Trans id="Digital annual general meeting:" />
                </FormLabel>
                {formState.errors.digitalAgm?.type === 'required' && (
                  <FormHelperText>
                    <Trans id="Required field" />
                  </FormHelperText>
                )}

                <RadioGroup value={field.value} onChange={e => field.onChange(e.target.value)}>
                  <Radio
                    label={<Trans id="The company shall be able to hold digital general meetings with postal voting" />}
                    value={GqlDigitalAgm.AllowedWithMailVote}
                  />
                  <Radio
                    label={
                      <Trans id="The company shall be able to hold digital general meetings, but without postal voting" />
                    }
                    value={GqlDigitalAgm.Allowed}
                  />
                  <Radio
                    label={<Trans id="The company shall not be able to hold digital general meetings" />}
                    value={GqlDigitalAgm.NotAllowed}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Stack>

        <Stack direction="row" justifyContent="flex-start">
          <Button type="submit" loading={loading}>
            <Trans id="Next" />
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
